import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icons for Leaflet in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const orangeIcon = L.divIcon({
  html: `<div style="background:#E8A020;width:18px;height:18px;border-radius:999px;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
  className: "",
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});
const slateIcon = L.divIcon({
  html: `<div style="background:#161712;width:18px;height:18px;border-radius:999px;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
  className: "",
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

/**
 * Props:
 *  - routes: [{ pickup:{lat,lng,city}, dropoff:{lat,lng,city}, color? }]
 *  - height: CSS height
 */
export default function RouteMap({ routes = [], height = 320 }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true, scrollWheelZoom: false }).setView([48.8566, 2.3522], 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© OpenStreetMap',
      maxZoom: 18,
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.stop();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // clear existing layers (except tiles)
    map.eachLayer((layer) => {
      if (!(layer instanceof L.TileLayer)) map.removeLayer(layer);
    });
    const bounds = [];
    routes.forEach((r, i) => {
      if (!r?.pickup || !r?.dropoff) return;
      const color = r.color || (i === 0 ? "#E8A020" : "#161712");
      L.polyline(
        [
          [r.pickup.lat, r.pickup.lng],
          [r.dropoff.lat, r.dropoff.lng],
        ],
        { color, weight: 3, opacity: 0.8, dashArray: i === 0 ? null : "6 8" }
      ).addTo(map);
      L.marker([r.pickup.lat, r.pickup.lng], { icon: orangeIcon })
        .bindTooltip(`Départ: ${r.pickup.city || ""}`)
        .addTo(map);
      L.marker([r.dropoff.lat, r.dropoff.lng], { icon: slateIcon })
        .bindTooltip(`Arrivée: ${r.dropoff.city || ""}`)
        .addTo(map);
      bounds.push([r.pickup.lat, r.pickup.lng], [r.dropoff.lat, r.dropoff.lng]);
    });
    if (bounds.length) map.fitBounds(bounds, { padding: [32, 32], animate: false });
  }, [routes]);

  return <div ref={containerRef} data-testid="route-map" style={{ height, width: "100%" }} className="rounded-md overflow-hidden border border-slate-200" />;
}
